/**
 * AI Selection Store
 *
 * Tracks query editor selections per tab and queues AI selection requests.
 * - Selections are updated by QueryEditorTab via Monaco selection events
 * - Requests are pushed by IPC events (selection.request.json) and applied by QueryEditorTab
 */

import { create } from 'zustand';
import type { EditorSelectionRange, SelectionRequest } from '../../../types/aiWorkspace';

export type AiTabSelection = {
  tabId: string;
  tabName: string;
  selection: EditorSelectionRange | null;
  selectedText: string;
  updatedAt: number;
};

export type AiSelectionRequestEnvelope = {
  sessionId: string;
  requestedAt: number;
  request: SelectionRequest;
};

interface AiSelectionState {
  selectionsByTabId: Map<string, AiTabSelection>;
  selectionRequestsByTabId: Map<string, AiSelectionRequestEnvelope>;

  setTabSelection: (selection: AiTabSelection) => void;
  removeTabSelection: (tabId: string) => void;

  setSelectionRequest: (envelope: AiSelectionRequestEnvelope) => void;
  clearSelectionRequest: (tabId: string) => void;
}

export const useAiSelectionStore = create<AiSelectionState>((set) => ({
  selectionsByTabId: new Map(),
  selectionRequestsByTabId: new Map(),

  setTabSelection: (selection) => {
    set((state) => {
      const next = new Map(state.selectionsByTabId);
      next.set(selection.tabId, selection);
      return { selectionsByTabId: next };
    });
  },

  removeTabSelection: (tabId) => {
    set((state) => {
      if (!state.selectionsByTabId.has(tabId)) return state;
      const next = new Map(state.selectionsByTabId);
      next.delete(tabId);
      return { selectionsByTabId: next };
    });
  },

  setSelectionRequest: (envelope) => {
    set((state) => {
      const tabId = envelope.request.tabId;
      const next = new Map(state.selectionRequestsByTabId);
      next.set(tabId, envelope);
      return { selectionRequestsByTabId: next };
    });
  },

  clearSelectionRequest: (tabId) => {
    set((state) => {
      if (!state.selectionRequestsByTabId.has(tabId)) return state;
      const next = new Map(state.selectionRequestsByTabId);
      next.delete(tabId);
      return { selectionRequestsByTabId: next };
    });
  },
}));

